import { useEffect, useState } from "react";
import { Breadcrumb, Empty, message, Tabs, Tag } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useQuery } from "../../common/utils";
import projectDefault from "../../img/project_default.jpg";
import {
  fetchMySaleActivityLearnInfo,
  fetchSaleActivityInfo,
  fetchSaleActivityLiveRoomList,
  fetchSaleActivityUserEnterLiveRoomUrl,
} from "./api";
import {
  SaleActivityInfo,
  SaleActivityLiveInfo,
  SaleActivityProjectInfo,
  SaleActivityTeachingActivityInfo,
} from "./types";
import { getSaleActivityTimeStatusText } from "./status";
import "./activity.scss";

const textOrDash = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "--" : value;

const peopleOrDash = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "--" : `${value}人`;

function getActivityResponseData(res: any) {
  return res?.data?.data || res?.data || res || {};
}

function getHostUnitText(info: SaleActivityInfo | null) {
  const source = (info || {}) as any;
  return textOrDash(
    source.hostUnit ||
      source.hostUnitName ||
      source.instituteName ||
      source.organizer ||
      source.organizerUnit ||
      source.sponsorUnit ||
      source.activityInfo?.hostUnit
  );
}

function InfoCell({ label, value }: { label: string; value: string | number }) {
  return (
    <>
      <div className="sale-activity-learn-info-label">{label}</div>
      <div className="sale-activity-learn-info-value">{value}</div>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <>
      <div className="sale-activity-learn-info-label">{label}</div>
      <div className="sale-activity-learn-info-value sale-activity-learn-info-value--wide">{value}</div>
    </>
  );
}

function StatusSummaryItem({ label, value }: { label: string; value: string | number }) {
  return (
    <span className="sale-activity-learn-status-summary__item">
      <em>{label}：</em>
      <strong>{value}</strong>
    </span>
  );
}

export default function SaleActivityLearnDetail() {
  const query = useQuery();
  const history = useHistory();
  const activityId = query.get("activityId") || "";
  const type = (query.get("type") || "user") as "expert" | "user";
  const [activityInfo, setActivityInfo] = useState<SaleActivityInfo | null>(null);
  const [projectList, setProjectList] = useState<SaleActivityProjectInfo[]>([]);
  const [liveActivityList, setLiveActivityList] = useState<SaleActivityLiveInfo[]>([]);
  const [teachingActivityList, setTeachingActivityList] = useState<SaleActivityTeachingActivityInfo[]>([]);

  useEffect(() => {
    if (!activityId) return;
    const loadActivityInfo = async () => {
      const learnRes = await fetchMySaleActivityLearnInfo(type, activityId);
      const learnData = getActivityResponseData(learnRes);
      const infoRes = await fetchSaleActivityInfo(activityId).catch(() => null);
      const detailData = getActivityResponseData(infoRes);
      const detailInfo = detailData.activityInfo || detailData || {};

      setActivityInfo({
        ...(learnData.activityInfo || learnData),
        ...detailInfo,
      });
      setProjectList(learnData.projectList || detailData.projectList || []);
      setLiveActivityList(learnData.liveActivityList || detailData.liveActivityList || []);
      setTeachingActivityList(learnData.teachingActivityList || detailData.teachingActivityList || []);
    };

    loadActivityInfo();
  }, [activityId, type]);

  if (!activityInfo) return <div className="sale-activity-page"><Empty description="暂无活动学习信息" /></div>;

  const isActivityEnded =
    activityInfo.activityLearnStatus === 3 ||
    activityInfo.activityLearnStatusText === "已结束";
  const isStudyEnded = (item: SaleActivityProjectInfo) =>
    isActivityEnded ||
    Number(item.studyStatus) === 2 ||
    item.studyStatusText === "已结束";

  const openProject = (item: SaleActivityProjectInfo) => {
    const studyStatus = Number(item.studyStatus);
    const timeRange = `${textOrDash(item.startTimeText)} - ${textOrDash(item.endTimeText)}`;
    if (isActivityEnded) {
      message.warning("活动已结束，无法进入点播课程");
      return;
    }
    if (studyStatus === 0 || item.studyStatusText === "未开始") {
      message.warning(`不在学习时间范围内，学习时间：${timeRange}`);
      return;
    }
    if (isStudyEnded(item)) {
      message.warning(`点播课程已结束，学习时间：${timeRange}`);
      return;
    }
    history.push(`/saleActivityProjectInfo?saleActivityId=${activityId}&projectId=${item.projectId}`);
  };

  const getOfflineDetailId = (item: SaleActivityTeachingActivityInfo) =>
    item.saleActivityDetailId || item.teachingActivityId;

  const enterUserLiveRoom = (item: SaleActivityLiveInfo) => {
    const saleActivityDetailId = item.saleActivityDetailId;
    if (!saleActivityDetailId) return;
    const saleActivityId = item.saleActivityId || activityId;

    fetchSaleActivityLiveRoomList({
      saleActivityId,
      saleActivityDetailId,
      currentOperatorType: 2,
    }).then((res) => {
      const room = (res.data?.liveRoomList || [])[0];
      if (!room?.liveActivityRoomId) {
        message.warning("暂无可进入的直播间");
        return;
      }

      fetchSaleActivityUserEnterLiveRoomUrl({
        saleActivityId: room.saleActivityId || saleActivityId,
        saleActivityDetailId: room.saleActivityDetailId || saleActivityDetailId,
        liveActivityRoomId: room.liveActivityRoomId,
      }).then((urlRes) => {
        const url = urlRes.data?.enterLiveRoomUrl;
        if (!url) {
          message.warning("暂无可进入的直播间链接");
          return;
        }
        window.open(url, "_blank");
      });
    });
  };

  const openLiveRoomList = (item: SaleActivityLiveInfo) => {
    const saleActivityDetailId = item.saleActivityDetailId;
    if (!saleActivityDetailId) return;
    if (type === "user") {
      enterUserLiveRoom(item);
      return;
    }
    const params = new URLSearchParams({
      saleActivityId: String(item.saleActivityId || activityId),
      saleActivityDetailId: String(saleActivityDetailId),
      type,
      liveActivityName: item.liveActivityName || "直播授课",
      cover: resourceCover,
    });
    history.push(`/saleActivityLiveRoomList?${params.toString()}`);
  };

  const resourceCover = activityInfo.pictureUrl || projectDefault;
  const activityTypeList = [
    liveActivityList.length > 0 ? "直播授课" : "",
    projectList.length > 0 ? "点播课程" : "",
    teachingActivityList.length > 0 ? "线下授课" : "",
  ].filter(Boolean);
  const activityTypeText = activityTypeList.length > 0 ? activityTypeList.join("、") : "--";
  const activityStatusText = activityInfo.activityLearnStatusText || "--";
  const hostUnitText = getHostUnitText(activityInfo);
  const expertApplyStatusText =
    activityInfo.expertHireResultStatusText ||
    activityInfo.expertAuditStatusText ||
    activityInfo.expertHireStatusText ||
    "--";
  const userApplyStatusText =
    activityInfo.userSignUpResultStatusText ||
    activityInfo.userAuditStatusText ||
    activityInfo.userSignUpStatusText ||
    "--";
  const expertRecruitStatusText = getSaleActivityTimeStatusText(activityInfo, "expert");
  const userRecruitStatusText = getSaleActivityTimeStatusText(activityInfo, "user");
  const userLimit = activityInfo.signUpUserNum || activityInfo.signUpNum || activityInfo.signUpNumLimit;
  const expertLimit = activityInfo.signUpExpertNum || activityInfo.expertNumLimit;
  const pointRuleText = activityInfo.activityRule || (activityInfo.pointTotal ? `${activityInfo.pointTotal}积分` : "--");

  return (
    <div className="sale-activity-learn-page">
      <div className="sale-activity-learn-breadcrumb">
        <Breadcrumb separator=">">
          <Breadcrumb.Item>我的活动</Breadcrumb.Item>
          <Breadcrumb.Item>{type === "expert" ? "专家活动" : "学员活动"}</Breadcrumb.Item>
          <Breadcrumb.Item>{type === "expert" ? "专家活动详情" : "学员活动详情"}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="sale-activity-learn-main">
        <div className="sale-activity-learn-title-row">
          <div className="sale-activity-learn-title-main">
            <h2>{activityInfo.name}</h2>
            <Tag color="orange">活动状态：{activityStatusText}</Tag>
          </div>
          <div className="sale-activity-learn-status-summary">
            <StatusSummaryItem label="学员报名" value={userApplyStatusText} />
            <StatusSummaryItem label="专家报名" value={expertApplyStatusText} />
          </div>
        </div>
        <div className="sale-activity-learn-summary">
          <img src={activityInfo.pictureUrl || projectDefault} alt={activityInfo.name} />
          <div className="sale-activity-learn-info-grid sale-activity-detail-info-grid">
            <InfoRow label="活动类型" value={activityTypeText} />
            <InfoRow label="主办单位" value={hostUnitText} />
            <InfoRow label="活动时间" value={`${textOrDash(activityInfo.beginTimeText || activityInfo.beginTime)} - ${textOrDash(activityInfo.endTimeText || activityInfo.endTime)}`} />
            <InfoRow label="活动状态" value={activityStatusText} />
          </div>
        </div>
      </div>

      <div className="sale-activity-learn-content">
        <Tabs defaultActiveKey="intro" className="sale-activity-learn-tabs">
          <Tabs.TabPane key="intro" tab="活动介绍">
            <div className="sale-activity-html" dangerouslySetInnerHTML={{ __html: activityInfo.activityContent || "暂无活动介绍" }} />
          </Tabs.TabPane>
          <Tabs.TabPane key="recruit" tab="招募信息">
            <div className="sale-activity-learn-info-grid sale-activity-detail-tab-info-grid">
              <InfoCell label="计划招募专家" value={peopleOrDash(expertLimit)} />
              <InfoCell label="专家招募状态" value={expertRecruitStatusText} />
              <InfoRow label="专家招募时间" value={`${textOrDash(activityInfo.expertHireBeginTime)} - ${textOrDash(activityInfo.expertHireEndTime)}`} />
              <InfoCell label="计划招募学员" value={peopleOrDash(userLimit)} />
              <InfoCell label="学员招募状态" value={userRecruitStatusText} />
              <InfoRow label="学员招募时间" value={`${textOrDash(activityInfo.signUpBeginTime)} - ${textOrDash(activityInfo.signUpEndTime)}`} />
              <InfoRow label="积分规则" value={pointRuleText} />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane key="expert" tab="专家招募通知">
            <div className="sale-activity-html" dangerouslySetInnerHTML={{ __html: activityInfo.expertHireContent || "暂无专家招募通知" }} />
          </Tabs.TabPane>
          <Tabs.TabPane key="user" tab="学员招募通知">
            <div className="sale-activity-html" dangerouslySetInnerHTML={{ __html: activityInfo.userHireContent || "暂无学员招募通知" }} />
          </Tabs.TabPane>
        </Tabs>
      </div>

      <div className="sale-activity-learn-resources">
        <div className="sale-activity-learn-resource-section">
          <h3>直播授课</h3>
          {liveActivityList.length > 0 ? (
            <div className="sale-activity-resource-list">
              {liveActivityList.map((item) => (
                <div className="sale-activity-resource-card sale-activity-resource-card--live" key={item.liveActivityId} onClick={() => openLiveRoomList(item)}>
                  <img src={resourceCover} alt={item.liveActivityName || "直播授课"} />
                  <h4>{item.liveActivityName}</h4>
                  <p>开始时间：{textOrDash(item.beginTimeText || item.beginTime)}</p>
                  <p>结束时间：{textOrDash(item.endTimeText || item.endTime)}</p>
                </div>
              ))}
            </div>
          ) : <Empty description="暂无直播授课" />}
        </div>

        <div className="sale-activity-learn-resource-section">
          <h3>点播课程</h3>
          {projectList.length > 0 ? (
            <div className="sale-activity-resource-list">
              {projectList.map((item) => (
                <div className="sale-activity-resource-card" key={item.projectId} onClick={() => openProject(item)}>
                  <img src={item.projectPicture || item.pictureUrl || resourceCover} alt={item.projectName || "点播课程"} />
                  <h4>{item.projectName}</h4>
                  <p>开始时间：{textOrDash(item.startTimeText)}</p>
                  <p>结束时间：{textOrDash(item.endTimeText)}</p>
                </div>
              ))}
            </div>
          ) : <Empty description="暂无点播课程" />}
        </div>

        <div className="sale-activity-learn-resource-section">
          <h3>线下授课</h3>
          {teachingActivityList.length > 0 ? (
            <div className="sale-activity-resource-list">
              {teachingActivityList.map((item) => (
                <div className="sale-activity-resource-card" key={item.teachingActivityId} onClick={() => history.push(`/saleActivityOfflineDetail?saleActivityId=${activityId}&activityDetailId=${getOfflineDetailId(item)}&teachingActivityId=${item.teachingActivityId}&type=${type}`)}>
                  <img src={resourceCover} alt={item.teachingActivityName || "线下授课"} />
                  <h4>{item.teachingActivityName}</h4>
                  <p>开始时间：{textOrDash(item.beginTimeText || item.startTime)}</p>
                  <p><EnvironmentOutlined /> {textOrDash(item.activityAddress)}</p>
                </div>
              ))}
            </div>
          ) : <Empty description="暂无线下授课" />}
        </div>
      </div>

    </div>
  );
}
